import { useState } from 'react';
import { Icon } from '../common';
import { useClickOutside } from '../../lib/useClickOutside';
import type { DatePickerMode } from './datePickerMode';
import { monthMatrix, stepDate, isSameDay } from './dateGrid';

// The top bar's contextual date picker (app-shell-redesign AC4). A thin render
// layer over the pure logic in datePickerMode / dateGrid. `mode` is resolved by
// the page (see datePickerMode); when null the control renders disabled/greyed.

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MO_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WD = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function label(mode: DatePickerMode, d: Date): string {
  if (mode === 'year') return `${d.getFullYear()}`;
  if (mode === 'month') return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  return `${DOW[d.getDay()]} ${d.getDate()} ${MO_SHORT[d.getMonth()]}`;
}

export interface DatePickerProps {
  /** Resolved by the active page; null renders the control disabled. */
  mode: DatePickerMode | null;
  value: Date;
  onChange: (d: Date) => void;
  /** Marks "today" in the popover; omit to skip the highlight (keeps tests pure). */
  today?: Date;
}

export function DatePicker({ mode, value, onChange, today }: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState({ year: value.getFullYear(), month: value.getMonth() });
  // Close the popover when the user clicks anywhere outside the control.
  const ref = useClickOutside<HTMLDivElement>(() => setOpen(false));

  if (!mode) {
    return (
      <div className="datectl dp-disabled" aria-disabled="true">
        <button type="button" className="dp-step" tabIndex={-1} aria-hidden="true"><Icon name="left" size={16} /></button>
        <span className="dp-label"><Icon name="calendar" size={15} className="cal" /><span>{label('day', value)}</span></span>
        <button type="button" className="dp-step" tabIndex={-1} aria-hidden="true"><Icon name="right" size={16} /></button>
      </div>
    );
  }

  const openPop = () => {
    setView({ year: value.getFullYear(), month: value.getMonth() });
    setOpen((o) => !o);
  };
  const pick = (d: Date) => { onChange(d); setOpen(false); };

  return (
    <div className="datectl" ref={ref}>
      <button type="button" className="dp-step" aria-label="Previous" onClick={() => onChange(stepDate(value, mode, -1))}>
        <Icon name="left" size={16} />
      </button>
      <button type="button" className="dp-label" aria-expanded={open} onClick={openPop}>
        <Icon name="calendar" size={15} className="cal" />
        <span>{label(mode, value)}</span>
      </button>
      <button type="button" className="dp-step" aria-label="Next" onClick={() => onChange(stepDate(value, mode, 1))}>
        <Icon name="right" size={16} />
      </button>

      {open && (mode === 'day' ? (
        <div className="popover datepop" role="dialog">
          <div className="dp-head">
            <button type="button" className="dp-nav" aria-label="Previous month"
              onClick={() => setView((v) => v.month === 0 ? { year: v.year - 1, month: 11 } : { year: v.year, month: v.month - 1 })}>
              <Icon name="left" size={16} />
            </button>
            <span className="dp-title">{MONTHS[view.month]} {view.year}</span>
            <button type="button" className="dp-nav" aria-label="Next month"
              onClick={() => setView((v) => v.month === 11 ? { year: v.year + 1, month: 0 } : { year: v.year, month: v.month + 1 })}>
              <Icon name="right" size={16} />
            </button>
          </div>
          <div className="dp-grid">
            {WD.map((w) => <div key={w} className="dp-wd">{w}</div>)}
            {monthMatrix(view.year, view.month).flat().map((d, i) =>
              d === null
                ? <button key={i} className="dp-day dp-blank" tabIndex={-1} aria-hidden="true" />
                : <button key={i} type="button"
                    className={'dp-day' + (today && isSameDay(d, today) ? ' dp-today' : '') + (isSameDay(d, value) ? ' dp-sel' : '')}
                    onClick={() => pick(d)}>{d.getDate()}</button>
            )}
          </div>
        </div>
      ) : mode === 'month' ? (
        <div className="popover datepop" role="dialog">
          <div className="dp-head">
            <button type="button" className="dp-nav" aria-label="Previous year" onClick={() => setView((v) => ({ ...v, year: v.year - 1 }))}><Icon name="left" size={16} /></button>
            <span className="dp-title">{view.year}</span>
            <button type="button" className="dp-nav" aria-label="Next year" onClick={() => setView((v) => ({ ...v, year: v.year + 1 }))}><Icon name="right" size={16} /></button>
          </div>
          <div className="dp-months">
            {MO_SHORT.map((m, i) =>
              <button key={m} type="button"
                className={'dp-mo' + (view.year === value.getFullYear() && i === value.getMonth() ? ' dp-sel' : '')}
                onClick={() => pick(new Date(view.year, i, 1))}>{m}</button>
            )}
          </div>
        </div>
      ) : (
        // Year mode — a 12-year window; prev/next shift the window by 12 years.
        <div className="popover datepop" role="dialog">
          <div className="dp-head">
            <button type="button" className="dp-nav" aria-label="Earlier years" onClick={() => setView((v) => ({ ...v, year: v.year - 12 }))}><Icon name="left" size={16} /></button>
            <span className="dp-title">{view.year - 5}–{view.year + 6}</span>
            <button type="button" className="dp-nav" aria-label="Later years" onClick={() => setView((v) => ({ ...v, year: v.year + 12 }))}><Icon name="right" size={16} /></button>
          </div>
          <div className="dp-months">
            {Array.from({ length: 12 }, (_, i) => view.year - 5 + i).map((y) =>
              <button key={y} type="button"
                className={'dp-mo' + (y === value.getFullYear() ? ' dp-sel' : '')}
                onClick={() => pick(new Date(y, 0, 1))}>{y}</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
